import { useEffect, useState } from 'react'
import {SlidersHorizontal, Search, Building2, CheckCircle2, XCircle, ChevronDown, ChevronUp} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'
import {
  useCompanyAspects,
  mergeWithDefaults,
  defaultCompanyAspects,
  type CompanyAspectsDoc,
  type CompanyAspect,
} from '@/hooks/useCompanyAspects'

interface CompanyRow {
  companyUserId: string
  companyName: string
  email: string
  aspects: CompanyAspect[]
  isCustomized: boolean
}

function AdminAspectosEvaluacion() {
  const { fetchAll } = useCompanyAspects()
  const [rows, setRows] = useState<CompanyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        // Cargar empresas y configuraciones en paralelo
        const [companiesRes, configs] = await Promise.all([
          lumi.entities.companies.list({ limit: 500 }),
          fetchAll(),
        ])
        const companies = (companiesRes.list as any[]) || []
        const configMap = new Map<string, CompanyAspectsDoc>()
        for (const cfg of configs) configMap.set(cfg.companyUserId, cfg)

        const result: CompanyRow[] = companies.map((c) => {
          const cfg = configMap.get(c.userId)
          const name = c.tradeName ?? c.commercialName ?? c.displayName ?? c.email ?? 'Empresa'
          return {
            companyUserId: c.userId,
            companyName: name,
            email: c.email ?? '',
            aspects: cfg ? mergeWithDefaults(cfg.aspects) : defaultCompanyAspects(),
            isCustomized: Boolean(cfg),
          }
        })
        setRows(result)
      } catch (e) {
        console.error('Error al cargar aspectos por empresa:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [fetchAll])

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return r.companyName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
  })

  return (
    <AdminLayout
      title="Aspectos de evaluación por empresa"
      subtitle="Consulta los aspectos que cada empresa evalúa a sus colaboradores"
    >
      <div className="space-y-5">
        {/* Buscador */}
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar empresa por nombre o correo..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center text-gray-400">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center text-gray-400">
            No se encontraron empresas.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((row) => {
              const isOpen = expanded === row.companyUserId
              const enabled = row.aspects.filter((a) => a.enabled)
              const customCount = row.aspects.filter((a) => a.custom).length
              return (
                <div key={row.companyUserId} className="rounded-2xl border border-gray-200 bg-white">
                  <button
                    onClick={() => setExpanded(isOpen ? null : row.companyUserId)}
                    className="flex w-full items-center gap-4 px-5 py-4 text-left"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-900">
                      <Building2 size={18} className="text-amber-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">{row.companyName}</p>
                      <p className="truncate text-xs text-gray-400">{row.email}</p>
                    </div>
                    <div className="hidden items-center gap-2 sm:flex">
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        {enabled.length} activos
                      </span>
                      {customCount > 0 && (
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                          {customCount} propios
                        </span>
                      )}
                      {!row.isCustomized && (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                          Configuración base
                        </span>
                      )}
                    </div>
                    {isOpen ? (
                      <ChevronUp size={18} className="shrink-0 text-gray-400" />
                    ) : (
                      <ChevronDown size={18} className="shrink-0 text-gray-400" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-100 px-5 py-4">
                      <div className="mb-3 flex items-center gap-2 text-sm text-gray-500 sm:hidden">
                        <SlidersHorizontal size={14} />
                        {enabled.length} activos · {customCount} propios
                      </div>
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {row.aspects.map((a) => (
                          <li
                            key={a.key}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                              a.enabled
                                ? 'border-emerald-100 bg-emerald-50/40'
                                : 'border-gray-100 bg-gray-50 opacity-60'
                            }`}
                          >
                            {a.enabled ? (
                              <CheckCircle2 size={15} className="shrink-0 text-emerald-500" />
                            ) : (
                              <XCircle size={15} className="shrink-0 text-gray-300" />
                            )}
                            <span className="flex-1 truncate text-gray-800">{a.label}</span>
                            {a.custom && (
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                                Propio
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminAspectosEvaluacion
